/* PatientIdCard — clinic-branded "member card" with pixel-perfect download.
 *
 * iter98.49 — Rewritten to use INLINE PIXEL STYLES throughout the card's
 * rendered area + html-to-image (not html2canvas). The previous version
 * relied on Tailwind utility classes which html2canvas mis-rendered:
 *   • `w-12 h-12 object-cover` on <img> → photo rendered at natural size
 *   • `line-clamp-2` → name was clipped vertically
 *   • flexbox + Tailwind sizes → stretched aspect ratio
 *
 * Fix strategy: every dimension on the card-render branch is a plain
 * inline style (`style={{ width: 48, height: 48, objectFit: 'cover' }}`)
 * so the html-to-image rasterizer doesn't depend on Tailwind. The
 * surrounding shell (info strip + Download button) keeps Tailwind.
 *
 * Why html-to-image over html2canvas:
 *   • Native support for object-fit / object-position on images
 *   • Better flexbox / inline-block layout handling
 *   • Embeds web fonts properly
 *   • Honors the actual rendered DOM rect (no manual width/height needed)
 *
 * Layout (440 × 275 px):
 *
 *   ┌───────────────────────────────────────────┐
 *   │ [BS]  Bright Smiles Dental, Bandra        │  ← 76px tall row
 *   │       PATIENT MEMBER CARD                 │
 *   ├───────────────────────────────────────────┤
 *   │                                           │
 *   │  ┌──┐  John Smith              ▓▓▓▓▓▓     │  ← identity row
 *   │  │JS│  INV-PT-000077           ▓ QR ▓     │     ~115px
 *   │  └──┘                          ▓▓▓▓▓▓     │
 *   │                                           │
 *   ├───────────────────────────────────────────┤
 *   │ 📅 Member since Jun 2026   ✓ Verified     │  ← 40px footer
 *   │    Mumbai            Powered by Invisique │
 *   └───────────────────────────────────────────┘
 */
import React, { useRef, useState, useEffect } from "react";
import { QRCodeSVG } from "qrcode.react";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Download, CircleNotch, ShareNetwork } from "@phosphor-icons/react";


/* Compose the QR payload as a vCard 3.0 string — scanning with any
   phone camera offers "Add to contacts" → clinic saved to address book. */
function buildVCard({ clinic }) {
    if (!clinic || !clinic.name) return "https://invisique.in";
    const safe = (s) => (s || "").replace(/[;,\\\n\r]/g, " ").trim();
    const lines = [
        "BEGIN:VCARD",
        "VERSION:3.0",
        `FN:${safe(clinic.name)}`,
        `ORG:${safe(clinic.name)}`,
    ];
    if (clinic.phone)   lines.push(`TEL;TYPE=WORK,VOICE:${safe(clinic.phone)}`);
    if (clinic.email)   lines.push(`EMAIL;TYPE=WORK:${safe(clinic.email)}`);
    if (clinic.address) lines.push(`ADR;TYPE=WORK:;;${safe(clinic.address)};;;;`);
    lines.push("NOTE:Saved from your Invisique™\u2122 patient card");
    lines.push("END:VCARD");
    return lines.join("\n");
}

function clinicInitials(name) {
    if (!name) return "C";
    const words = String(name).replace(/[,.()]/g, " ").trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) return "C";
    if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
    return (words[0][0] + words[1][0]).toUpperCase();
}

/* Smart name truncation — keeps the first name + initials of the rest
   so it always fits within ~16ch without clipping. */
function fitName(name, maxLen = 22) {
    if (!name) return "—";
    if (name.length <= maxLen) return name;
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, maxLen - 1) + "…";
    const first = parts[0];
    const initials = parts.slice(1).map((w) => w[0]?.toUpperCase()).join(".");
    const candidate = `${first} ${initials}.`;
    if (candidate.length <= maxLen) return candidate;
    return first.slice(0, maxLen - 1) + "…";
}


export default function PatientIdCard({ form, user, careTeam, caseCode }) {
    const cardRef = useRef(null);
    const [downloading, setDownloading] = useState(false);
    const [sharing, setSharing] = useState(false);

    // iter98.51 — Detect Web Share API + file-share capability up
    // front so we can conditionally render the Share button. The
    // canShare({files}) check is the only reliable feature detection
    // — `navigator.share` alone exists on Chromium desktop but won't
    // accept files there.
    const [canShareFiles, setCanShareFiles] = useState(false);
    useEffect(() => {
        try {
            const probeFile = new File([new Blob([""], { type: "image/png" })], "probe.png", { type: "image/png" });
            const ok = typeof navigator !== "undefined"
                && typeof navigator.canShare === "function"
                && navigator.canShare({ files: [probeFile] });
            setCanShareFiles(!!ok);
        } catch {
            setCanShareFiles(false);
        }
    }, []);

    const code = user?.code || user?.patient_code || "";
    const initials = (form?.name || "").trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase()).join("") || "?";
    const memberSince = user?.created_at
        ? new Date(user.created_at).toLocaleDateString("en-IN", { month: "short", year: "numeric" })
        : null;

    const doctor = careTeam?.doctor || null;
    const clinicName = doctor?.clinic_name || null;
    const clinicLogo = doctor?.clinic_logo || null;
    const clinicAddress = doctor?.clinic_address || doctor?.address || null;
    const clinicPhone = doctor?.clinic_phone || doctor?.phone || null;
    const clinicEmail = doctor?.email || null;
    const doctorName = doctor?.name || null;

    const displayName = fitName(form?.name || "—", 22);

    const qrPayload = buildVCard({
        clinic: {
            name: clinicName,
            phone: clinicPhone,
            email: clinicEmail,
            address: clinicAddress,
        },
    });

    const safeClinic = (clinicName || "invisique").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 30) || "clinic";
    const downloadFilename = `${safeClinic}-member-card-${code || "patient"}.png`;

    // Shared PNG generator — used by both Download + Share so the
    // bytes are identical regardless of which path the patient takes.
    const generateCardPng = async () => {
        return await toPng(cardRef.current, {
            cacheBust: true,
            pixelRatio: 3,
            width: 440,
            height: 275,
            style: { transform: "none", margin: "0" },
        });
    };

    const dataUrlToFile = async (dataUrl, filename) => {
        const res = await fetch(dataUrl);
        const blob = await res.blob();
        return new File([blob], filename, { type: "image/png" });
    };

    const handleDownload = async () => {
        if (!cardRef.current) return;
        setDownloading(true);
        try {
            const dataUrl = await generateCardPng();
            const a = document.createElement("a");
            a.href = dataUrl;
            a.download = downloadFilename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            toast.success("Member card saved to your downloads");
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error("Member card PNG export failed:", err);
            toast.error("Couldn't generate the image — try again in a moment.");
        } finally { setDownloading(false); }
    };

    // iter98.51 — Native share sheet (Web Share API). Opens the
    // phone's "Share to…" picker so the patient can WhatsApp it to
    // family, save to Apple Wallet, etc. Falls back to download on
    // desktop where file-share isn't supported.
    const handleShare = async () => {
        if (!cardRef.current) return;
        setSharing(true);
        try {
            const dataUrl = await generateCardPng();
            const file = await dataUrlToFile(dataUrl, downloadFilename);

            if (canShareFiles && navigator.canShare({ files: [file] })) {
                await navigator.share({
                    files: [file],
                    title: clinicName ? `My ${clinicName} member card` : "My Invisique™ member card",
                    text: clinicName
                        ? `My ${clinicName} member card · ${code}`
                        : `My Invisique™ patient card · ${code}`,
                });
                // No success toast — the share sheet itself confirms.
            } else {
                // Fallback: treat share-tap as download on desktop.
                const a = document.createElement("a");
                a.href = dataUrl;
                a.download = downloadFilename;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                toast("Share isn't supported here — we saved the card to your downloads instead.", { duration: 4000 });
            }
        } catch (err) {
            // AbortError is the user dismissing the share sheet — silent.
            if (err && err.name === "AbortError") return;
            // eslint-disable-next-line no-console
            console.error("Member card share failed:", err);
            toast.error("Couldn't open the share sheet — try the Download button instead.");
        } finally { setSharing(false); }
    };

    // ────────────────────────────────────────────────────────────
    // Style constants — pinned in pixels so html-to-image renders
    // the same as on-screen, regardless of Tailwind utility quirks.
    // ────────────────────────────────────────────────────────────
    const S = {
        card: {
            width: 440,
            height: 275,
            position: "relative",
            overflow: "hidden",
            borderRadius: 16,
            color: "#ffffff",
            background: "linear-gradient(135deg, hsl(193 100% 12%) 0%, hsl(192 100% 20%) 45%, hsl(173 60% 30%) 100%)",
            boxShadow: "0 24px 60px -12px rgba(0,0,0,0.35)",
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif",
        },
        blob1: { position: "absolute", top: -60, right: -60, width: 180, height: 180, borderRadius: "50%", background: "rgba(255,255,255,0.06)", pointerEvents: "none" },
        blob2: { position: "absolute", bottom: -70, left: "40%", width: 150, height: 150, borderRadius: "50%", background: "rgba(255,255,255,0.04)", pointerEvents: "none" },
        body: { position: "relative", height: "100%", padding: 20, display: "flex", flexDirection: "column", boxSizing: "border-box" },

        /* Row 1 — Clinic brand */
        clinicRow: { display: "flex", alignItems: "center", gap: 10, minWidth: 0, flex: "0 0 auto" },

        /* Row 2 — Patient identity — vertically CENTERED in the leftover space */
        identityRow: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flex: "1 1 auto" },
        identityLeft:  { display: "flex", alignItems: "center", gap: 12, minWidth: 0, flex: "1 1 auto" },
        clinicLogoImg: { width: 36, height: 36, borderRadius: 6, objectFit: "contain", background: "#ffffff", padding: 2, flexShrink: 0, boxSizing: "border-box" },
        clinicInitialsBox: {
            width: 36, height: 36, borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center",
            fontWeight: 700, fontSize: 13, color: "#ffffff",
            background: "rgba(255,255,255,0.16)", border: "1px solid rgba(255,255,255,0.20)", boxSizing: "border-box", flexShrink: 0,
        },
        clinicName: { fontSize: 15, fontWeight: 700, lineHeight: 1.15, letterSpacing: "-0.01em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
        clinicCardLabel: { fontSize: 9, textTransform: "uppercase", letterSpacing: "0.22em", color: "rgba(255,255,255,0.65)", fontWeight: 600, marginTop: 2 },

        /* Row 2 — Patient identity */
        patientPhoto:  { width: 48, height: 48, borderRadius: "50%", objectFit: "cover", border: "2px solid rgba(255,255,255,0.40)", flexShrink: 0, boxSizing: "border-box", display: "block" },
        patientInitialsBox: {
            width: 48, height: 48, borderRadius: "50%",
            background: "rgba(255,255,255,0.12)", border: "2px solid rgba(255,255,255,0.40)",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "#ffffff", fontSize: 16, fontWeight: 700, flexShrink: 0, boxSizing: "border-box",
        },
        patientText: { minWidth: 0, flex: "1 1 auto" },
        patientName: {
            fontSize: 15, fontWeight: 600, letterSpacing: "-0.01em", lineHeight: 1.2,
            color: "#ffffff",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        },
        patientCode: { fontSize: 10, fontFamily: "ui-monospace, 'SF Mono', 'Roboto Mono', monospace", letterSpacing: "0.08em", color: "rgba(255,255,255,0.85)", marginTop: 4 },

        qrWrap: { background: "#ffffff", borderRadius: 6, padding: 5, flexShrink: 0, lineHeight: 0 },

        /* Row 3 — Footer — pinned to the bottom */
        footerRow: { display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 12, color: "rgba(255,255,255,0.75)", flex: "0 0 auto" },
        memberSince: { fontSize: 9, textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600 },
        clinicAddr:  { fontSize: 9, color: "rgba(255,255,255,0.70)", marginTop: 2, maxWidth: 230, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
        verifiedPill:{ fontSize: 8, textTransform: "uppercase", letterSpacing: "0.10em", color: "rgba(255,255,255,0.70)", fontWeight: 700, display: "flex", alignItems: "center", gap: 4, justifyContent: "flex-end" },
        poweredBy:   { fontSize: 8.5, color: "rgba(255,255,255,0.62)", marginTop: 4, letterSpacing: "0.04em", textAlign: "right" },
    };

    return (
        <div className="space-y-4" data-testid="patient-id-card-wrap">
            {/* The card itself — pure inline styles to guarantee html-to-image
                renders pixel-perfect (Tailwind utilities have known issues
                in canvas rasterizers). */}
            <div ref={cardRef} style={S.card} data-testid="patient-id-card">
                <div style={S.blob1} aria-hidden="true" />
                <div style={S.blob2} aria-hidden="true" />

                <div style={S.body}>
                    {/* ── Row 1: Clinic brand ── */}
                    <div style={S.clinicRow}>
                        {clinicLogo ? (
                            <img
                                src={clinicLogo}
                                alt={clinicName || "Clinic"}
                                style={S.clinicLogoImg}
                                crossOrigin="anonymous"
                            />
                        ) : (
                            <div style={S.clinicInitialsBox} data-testid="card-clinic-initials">
                                {clinicInitials(clinicName)}
                            </div>
                        )}
                        <div style={{ minWidth: 0, flex: "1 1 auto" }}>
                            <div style={S.clinicName} data-testid="card-clinic-name">
                                {clinicName || "Your Clinic"}
                            </div>
                            <div style={S.clinicCardLabel}>Patient Member Card</div>
                        </div>
                    </div>

                    {/* ── Row 2: Patient identity ── */}
                    <div style={S.identityRow}>
                        <div style={S.identityLeft}>
                            {form?.photo_data_url ? (
                                <img
                                    src={form.photo_data_url}
                                    alt={form.name || "Patient"}
                                    style={S.patientPhoto}
                                    crossOrigin="anonymous"
                                />
                            ) : (
                                <div style={S.patientInitialsBox}>{initials}</div>
                            )}
                            <div style={S.patientText}>
                                <div style={S.patientName} data-testid="card-patient-name" title={form?.name || ""}>
                                    {displayName}
                                </div>
                                <div style={S.patientCode} data-testid="card-patient-code">
                                    {code || "—"}
                                </div>
                            </div>
                        </div>

                        <div style={S.qrWrap} data-testid="card-qr">
                            <QRCodeSVG
                                value={qrPayload}
                                size={64}
                                level="M"
                                bgColor="#ffffff"
                                fgColor="hsl(193 100% 12%)"
                            />
                        </div>
                    </div>

                    {/* ── Row 3: Footer ── */}
                    <div style={S.footerRow}>
                        <div>
                            {memberSince && (
                                <div style={S.memberSince}>📅 Member since {memberSince}</div>
                            )}
                            {clinicAddress && (
                                <div style={S.clinicAddr}>{clinicAddress}</div>
                            )}
                        </div>
                        <div>
                            <div style={S.verifiedPill}>
                                <span style={{ display: "inline-block", width: 8, height: 8, background: "currentColor", clipPath: "polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)" }} aria-hidden="true" />
                                Verified
                            </div>
                            <div style={S.poweredBy} data-testid="card-powered-by">
                                Powered by Invisique™<sup style={{ fontSize: 6 }}>™</sup>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Below-the-card info strip — Tailwind here is fine, this isn't captured */}
            <div style={{ maxWidth: 440 }}>
                <div className="grid grid-cols-3 gap-2 mt-3">
                    <CardStat label="Doctor" value={doctorName || "—"} />
                    <CardStat label="Clinic" value={clinicName || "—"} />
                    <CardStat label="Case" value={caseCode || "—"} mono />
                </div>

                <div className="mt-4 flex items-center gap-2 flex-wrap">
                    <Button
                        size="sm"
                        onClick={handleDownload}
                        disabled={downloading || sharing || !code}
                        className="rounded-full"
                        data-testid="download-id-card-btn"
                    >
                        {downloading ? (
                            <CircleNotch size={13} className="animate-spin mr-1.5" />
                        ) : (
                            <Download size={13} className="mr-1.5" />
                        )}
                        {downloading ? "Saving…" : "Download as PNG"}
                    </Button>

                    {/* iter98.51 — Share button uses the native share
                        sheet on mobile (WhatsApp, Photos, Wallet, etc.)
                        Falls back to download on desktop. Only renders
                        when navigator.canShare({files}) is true so we
                        don't waste pixels on desktop browsers that
                        can't share files. */}
                    {canShareFiles && (
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={handleShare}
                            disabled={downloading || sharing || !code}
                            className="rounded-full"
                            data-testid="share-id-card-btn"
                        >
                            {sharing ? (
                                <CircleNotch size={13} className="animate-spin mr-1.5" />
                            ) : (
                                <ShareNetwork size={13} className="mr-1.5" />
                            )}
                            {sharing ? "Sharing…" : "Share"}
                        </Button>
                    )}

                    <span className="text-[11px] text-muted-foreground">
                        {canShareFiles
                            ? "Save to your wallet or send to family."
                            : "Show this at reception for faster check-in."}
                    </span>
                </div>
            </div>
        </div>
    );
}


function CardStat({ label, value, mono }) {
    return (
        <div className="rounded-lg border border-border bg-card px-2.5 py-2 min-w-0">
            <div className="text-[9px] uppercase tracking-wider text-muted-foreground">{label}</div>
            <div className={`text-xs font-semibold truncate mt-0.5 ${mono ? "font-mono" : ""}`}>
                {value}
            </div>
        </div>
    );
}
